import { Button, Group, Modal, Select, Stack, Text, Title } from '@mantine/core';
import React, { useEffect, useMemo, useState } from 'react';
import { getMedia } from '../../utility/util';

type Props = {
    data: any;
    loading?: boolean;
    loadingAddConnection?: boolean;
    onClose: () => void;
    onConfirm: (payload: { oauth: any; account: any }) => void;
    onAddConnection?: () => void;
};

export default function CustomerAccountLinkModal({
    data,
    loading = false,
    loadingAddConnection = false,
    onClose,
    onConfirm,
    onAddConnection,
}: Props) {
    const oauths = useMemo(() => (data?.oauths ?? []).filter(Boolean), [data]);

    const [oauthId, setOauthId] = useState<string | null>(null);
    const [accountId, setAccountId] = useState<string | null>(null);

    const selectedOauth = useMemo(
        () => oauths.find((oauth) => oauth?._id === oauthId),
        [oauths, oauthId]
    );

    const accounts = useMemo(() => selectedOauth?.data?.accounts ?? [], [selectedOauth]);
    const selectedAccount = useMemo(
        () => accounts.find((account) => account?.id === accountId),
        [accounts, accountId]
    );

    useEffect(() => {
        if (!data) {
            setOauthId(null);
            setAccountId(null);
            return;
        }

        const firstOauth = (data?.oauths ?? [])[0];
        setOauthId(firstOauth?._id ?? null);
    }, [data]);

    const mediaTitle = getMedia(data?.origin)?.title ?? data?.origin ?? 'Rede social';
    const canConfirm = !!selectedOauth && !!selectedAccount;

    return <Modal
        opened={!!data}
        onClose={onClose}
        title={<Title order={4}>Vincular conta ao cliente</Title>}
    >
        <Stack>
            <Text size="sm">
                Cliente: <strong>{data?.customerName ?? 'Sem nome'}</strong>
            </Text>
            <Text size="sm">
                Rede: <strong>{mediaTitle}</strong>
            </Text>

            <Select
                label="Conta"
                value={oauthId}
                searchable
                onChange={setOauthId}
                data={oauths.map((oauth) => ({
                    value: oauth?._id,
                    label: oauth?.data?.me?.name
                        ?? oauth?.data?.me?.username
                        ?? oauth?.data?.me?.email
                        ?? `OAuth ${oauth?._id}`,
                }))}
                withinPortal
            />

            <Select
                label="Página"
                value={accountId}
                searchable
                onChange={setAccountId}
                data={accounts.map((account) => ({
                    value: account?.id,
                    label: account?.title ?? account?.name ?? account?.id,
                }))}
                nothingFound="Nenhuma página encontrada nessa conta"
                withinPortal
            />

            <Group style={{ justifyContent: 'flex-end' }}>
                {onAddConnection && (
                    <Button
                        variant='light'
                        loading={loadingAddConnection}
                        onClick={onAddConnection}
                    >
                        Nova conexão
                    </Button>
                )}
                <Button variant='default' onClick={onClose}>Cancelar</Button>
                <Button
                    loading={loading}
                    disabled={!canConfirm}
                    onClick={() => onConfirm({ oauth: selectedOauth, account: selectedAccount })}
                >
                    Vincular
                </Button>
            </Group>
        </Stack>
    </Modal>
}
